import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import ResizeObserver from 'resize-observer-polyfill';

type SizeState = {
  width: number;
  height: number;
};

type UseResizeObserver = (
  elementRef?: RefObject<HTMLElement>,
  wait?: number
) => number[];

export const useResizeObserver: UseResizeObserver = (
  elementRef,
  wait = 1000
) => {
  const [size, setSize] = useState<SizeState>({
    width: elementRef ? 0 : window.innerWidth || 0,
    height: 0
  });
  const htmlRef = useRef<HTMLElement>(document.documentElement);
  const ref = elementRef || htmlRef;
  const handleResize = useCallback((entries: ResizeObserverEntry[]): void => {
    if (Array.isArray(entries)) {
      const { contentRect } = entries[0];
      const { width, height } = contentRect;
      setSize({ width, height });
    }
  }, []);

  useEffect(() => {
    if (!ref.current) {
      return undefined;
    }

    let RO: ResizeObserver | null = new ResizeObserver(
      debounce(handleResize, wait)
    );

    // use debounce for observing not immediately
    RO.observe(ref.current);

    return () => {
      RO?.disconnect();
      RO = null;
    };
  }, [ref.current]);

  return Object.values(size);
};
